import { useState, useEffect } from "react";
import { api } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Copy, ChevronDown, ChevronRight, Link, Trash2, RefreshCw } from "lucide-react";

export default function Reconcile() {
  const currentYear = new Date().getFullYear();
  const [file, setFile] = useState(null);
  const [year, setYear] = useState(currentYear - 1); // Default to last year for reconciliation
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [expandedMonths, setExpandedMonths] = useState({});
  
  // Skipped transactions state
  const [skipped, setSkipped] = useState(null);
  const [loadingSkipped, setLoadingSkipped] = useState(false);
  const [categories, setCategories] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState({});

  useEffect(() => {
    loadCategories();
    loadMappings();
  }, []);

  const loadCategories = async () => {
    try {
      const res = await api.get("/categories");
      setCategories(res.data);
    } catch (error) {
      console.error("Failed to load categories", error);
    }
  };

  const loadMappings = async () => {
    try {
      const res = await api.get("/payee-mappings");
      setMappings(res.data.mappings || []);
    } catch (error) {
      console.error("Failed to load mappings", error);
    }
  };

  const loadSkipped = async () => {
    try {
      setLoadingSkipped(true);
      const res = await api.get(`/sync/skipped?year=${year}`);
      setSkipped(res.data);
    } catch (error) {
      toast.error("Failed to load skipped transactions");
    } finally {
      setLoadingSkipped(false);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleReconcile = async () => {
    if (!file) {
      toast.error("Please select a YNAB CSV file");
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("file", file);

      const res = await api.post(`/reconcile/ynab-csv?year=${year}&threshold=1`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setResult(res.data);
      
      if (res.data.months_aligned === 12) {
        toast.success("All months aligned!");
      } else {
        toast.info(`Found ${res.data.months_with_issues} month(s) with discrepancies`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reconcile");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (result?.copyable_summary) {
      await navigator.clipboard.writeText(result.copyable_summary);
      toast.success("Copied to clipboard!");
    }
  };

  const toggleMonth = (month) => {
    setExpandedMonths(prev => ({ ...prev, [month]: !prev[month] }));
  };

  const formatCurrency = (val) => {
    const absVal = Math.abs(val);
    const formatted = absVal.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    return val < 0 ? `-${formatted}` : formatted;
  };

  const getDiffColor = (diff) => {
    if (Math.abs(diff) < 1) return "text-slate-500";
    return diff > 0 ? "text-amber-600" : "text-rose-600";
  };

  const createMapping = async (payee) => {
    const categoryId = selectedCategory[payee];
    if (!categoryId) {
      toast.error("Please select a category");
      return;
    }
    
    try {
      await api.post(`/payee-mappings?payee_pattern=${encodeURIComponent(payee)}&category_id=${categoryId}`);
      toast.success(`Mapping created for "${payee}"`);
      loadMappings();
      loadSkipped();
    } catch (error) {
      toast.error("Failed to create mapping");
    }
  };

  const deleteMapping = async (mappingId) => {
    try {
      await api.delete(`/payee-mappings/${mappingId}`);
      toast.success("Mapping deleted");
      loadMappings();
    } catch (error) {
      toast.error("Failed to delete mapping");
    }
  };

  const groupedCategories = categories.reduce((acc, cat) => {
    const group = cat.group || "Other";
    if (!acc[group]) acc[group] = [];
    acc[group].push(cat);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold font-['Outfit'] text-slate-900">Reconciliation</h1>
        <p className="text-slate-600 mt-1">Compare YNAB export against MEFC data and manage payee mappings</p>
      </div>

      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Compare YNAB Export
          </CardTitle>
          <CardDescription>
            Export your YNAB "Income vs Expense" report as CSV and upload it here
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={String(currentYear - 2)}>{currentYear - 2}</SelectItem>
                <SelectItem value={String(currentYear - 1)}>{currentYear - 1}</SelectItem>
                <SelectItem value={String(currentYear)}>{currentYear}</SelectItem>
              </SelectContent>
            </Select>

            <label className="flex-1">
              <div className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-slate-400 transition-colors">
                <Upload className="h-4 w-4 text-slate-500" />
                <span className="text-sm text-slate-600">
                  {file ? file.name : "Choose YNAB CSV file..."}
                </span>
              </div>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
              />
            </label>

            <Button 
              onClick={handleReconcile} 
              disabled={!file || loading}
              data-testid="reconcile-btn"
            >
              {loading ? "Analyzing..." : "Reconcile"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Skipped Transactions Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-amber-500" />
                Skipped During Sync
              </CardTitle>
              <CardDescription>
                Transactions without category mappings that were skipped during YNAB sync
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={loadSkipped} disabled={loadingSkipped}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loadingSkipped ? 'animate-spin' : ''}`} />
              Load for {year}
            </Button>
          </div>
        </CardHeader>
        {skipped && (
          <CardContent className="space-y-4">
            {skipped.total_skipped === 0 ? (
              <p className="text-sm text-emerald-600">No skipped transactions for {year}!</p>
            ) : (
              <>
                <div className="text-sm text-slate-600">
                  {skipped.total_skipped} transactions from {skipped.unique_payees} payees were skipped
                </div>
                <div className="space-y-3">
                  {skipped.skipped_by_payee.map((item, idx) => (
                    <div key={idx} className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate" title={item.payee}>
                            {item.payee}
                          </div>
                          <div className="text-xs text-slate-500 mt-1">
                            {item.transaction_count} transaction(s) • Months: {item.months.join(", ")} • 
                            <span className={item.total_amount >= 0 ? "text-emerald-600" : "text-rose-600"}>
                              {" "}{formatCurrency(item.total_amount)}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Select 
                            value={selectedCategory[item.payee] || ""} 
                            onValueChange={(v) => setSelectedCategory(prev => ({...prev, [item.payee]: v}))}
                          >
                            <SelectTrigger className="w-[200px] h-8 text-xs">
                              <SelectValue placeholder="Map to category..." />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(groupedCategories).map(([group, cats]) => (
                                <div key={group}>
                                  <div className="px-2 py-1 text-xs font-semibold text-slate-500 bg-slate-100">
                                    {group}
                                  </div>
                                  {cats.map(cat => (
                                    <SelectItem key={cat.id} value={cat.id} className="text-xs">
                                      {cat.emoji && <span className="mr-1">{cat.emoji}</span>}
                                      {cat.name}
                                    </SelectItem>
                                  ))}
                                </div>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => createMapping(item.payee)}
                            disabled={!selectedCategory[item.payee]}
                          >
                            <Link className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        )}
      </Card>

      {/* Existing Mappings */}
      {mappings.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Payee Mappings</CardTitle>
            <CardDescription>
              These patterns will auto-map uncategorized transactions during future syncs
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {mappings.map((m) => (
                <div key={m.id} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                  <div>
                    <span className="font-mono text-xs bg-slate-200 px-1 rounded">{m.payee_pattern}</span>
                    <span className="mx-2">→</span>
                    <Badge variant="secondary">{m.category_name}</Badge>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => deleteMapping(m.id)}>
                    <Trash2 className="h-3 w-3 text-slate-400" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results Section */}
      {result && (
        <>
          {/* Summary Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  {result.months_aligned === 12 ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-amber-500" />
                  )}
                  Reconciliation Results
                </CardTitle>
                <Button variant="outline" size="sm" onClick={copyToClipboard}>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy for Chat
                </Button>
              </div>
              <CardDescription>
                {result.months_aligned}/12 months aligned
                {result.months_with_issues > 0 && (
                  <span className="text-amber-600 ml-2">
                    • {result.months_with_issues} month(s) with discrepancies
                  </span>
                )}
              </CardDescription>
            </CardHeader>
          </Card>

          {/* Month-by-Month Results */}
          <div className="space-y-3">
            {result.discrepancies.map((d) => (
              <Card 
                key={d.month} 
                className={d.is_aligned ? "border-emerald-200 bg-emerald-50/30" : "border-amber-200 bg-amber-50/30"}
              >
                <CardContent className="py-3">
                  <div 
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => !d.is_aligned && toggleMonth(d.month)}
                  >
                    <div className="flex items-center gap-3">
                      {d.is_aligned ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                      ) : (
                        <>
                          {expandedMonths[d.month] ? (
                            <ChevronDown className="h-4 w-4 text-amber-500" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-amber-500" />
                          )}
                        </>
                      )}
                      <span className="font-medium">{d.month_name}</span>
                    </div>
                    
                    <div className="flex items-center gap-6 text-sm">
                      <div className="text-right">
                        <div className="text-xs text-slate-500">Net Income</div>
                        <div className={d.is_aligned ? "text-emerald-600" : getDiffColor(d.net_income_diff)}>
                          {d.is_aligned ? (
                            formatCurrency(d.net_income_mefc)
                          ) : (
                            <span>
                              {formatCurrency(d.net_income_diff)} diff
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {!d.is_aligned && expandedMonths[d.month] && (
                    <div className="mt-4 pt-4 border-t border-amber-200 space-y-3">
                      {/* Summary row */}
                      <div className="grid grid-cols-4 gap-4 text-sm font-medium text-slate-600 border-b pb-2">
                        <div></div>
                        <div className="text-right">YNAB</div>
                        <div className="text-right">MEFC</div>
                        <div className="text-right">Diff</div>
                      </div>
                      
                      {/* Income */}
                      {Math.abs(d.income_diff) >= 1 && (
                        <div className="grid grid-cols-4 gap-4 text-sm">
                          <div className="font-medium text-emerald-700">Income</div>
                          <div className="text-right font-mono">{formatCurrency(d.income_ynab)}</div>
                          <div className="text-right font-mono">{formatCurrency(d.income_mefc)}</div>
                          <div className={`text-right font-mono font-medium ${getDiffColor(d.income_diff)}`}>
                            {d.income_diff >= 0 ? "+" : ""}{formatCurrency(d.income_diff)}
                          </div>
                        </div>
                      )}
                      
                      {/* Expenses */}
                      {Math.abs(d.expenses_diff) >= 1 && (
                        <div className="grid grid-cols-4 gap-4 text-sm">
                          <div className="font-medium text-rose-700">Expenses</div>
                          <div className="text-right font-mono">{formatCurrency(d.expenses_ynab)}</div>
                          <div className="text-right font-mono">{formatCurrency(d.expenses_mefc)}</div>
                          <div className={`text-right font-mono font-medium ${getDiffColor(d.expenses_diff)}`}>
                            {d.expenses_diff >= 0 ? "+" : ""}{formatCurrency(d.expenses_diff)}
                          </div>
                        </div>
                      )}

                      {/* Group breakdowns */}
                      {d.groups.map((g) => (
                        <div key={g.group} className="pl-4 border-l-2 border-slate-200 space-y-2">
                          <div className="grid grid-cols-4 gap-4 text-sm">
                            <div className={`font-medium ${
                              g.group === 'Income' ? 'text-emerald-600' :
                              g.group === 'Bills' ? 'text-amber-600' :
                              g.group === 'Needs' ? 'text-blue-600' : 'text-purple-600'
                            }`}>{g.group}</div>
                            <div className="text-right font-mono text-slate-600">{formatCurrency(g.ynab)}</div>
                            <div className="text-right font-mono text-slate-600">{formatCurrency(g.mefc)}</div>
                            <div className={`text-right font-mono font-medium ${getDiffColor(g.diff)}`}>
                              {g.diff >= 0 ? "+" : ""}{formatCurrency(g.diff)}
                            </div>
                          </div>
                          
                          {/* Category details */}
                          {g.categories.length > 0 && (
                            <div className="pl-4 space-y-1">
                              {g.categories.map((c, idx) => (
                                <div key={idx} className="grid grid-cols-4 gap-4 text-xs text-slate-500">
                                  <div className="truncate" title={c.category}>{c.category}</div>
                                  <div className="text-right font-mono">{formatCurrency(c.ynab)}</div>
                                  <div className="text-right font-mono">{formatCurrency(c.mefc)}</div>
                                  <div className={`text-right font-mono ${getDiffColor(c.diff)}`}>
                                    {c.diff >= 0 ? "+" : ""}{formatCurrency(c.diff)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Copyable Text Area (collapsed by default) */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Raw Output (for chat)</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-xs bg-slate-100 p-4 rounded-lg overflow-x-auto whitespace-pre-wrap font-mono">
                {result.copyable_summary}
              </pre>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
